import React, { FC, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { PageLink, PageTitle } from '../../../../rbts/layout/core';
import { KTSVG, SimpleAlert } from '../../../../rbts/helpers';
import { useObtenerConfiguraciones } from './hooks/SystemConfigHook';
import { SystemConfigModel } from './models/SystemConfigModel';
import { actualizarConfiguracionService } from '../../../service/systemconfig/SystemConfigService';

const breadCrumbs: Array<PageLink> = [];

const CATEGORY_LABELS: Record<string, string> = {
  EMAIL: 'Correo Electrónico',
  SISTEMA: 'Sistema',
  GENERAL: 'General',
};

const CATEGORY_ORDER = ['EMAIL', 'SISTEMA', 'GENERAL'];

function groupByCategory(configs: SystemConfigModel[]): Record<string, SystemConfigModel[]> {
  return configs.reduce((acc, c) => {
    if (!acc[c.category]) acc[c.category] = [];
    acc[c.category].push(c);
    return acc;
  }, {} as Record<string, SystemConfigModel[]>);
}

const SystemConfigPage: FC = () => {
  const [reload, setReload] = useState(0);
  const { configs, loading } = useObtenerConfiguraciones(reload);

  const [selected, setSelected] = useState<SystemConfigModel | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);

  const grouped = groupByCategory(configs);
  const categories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  function openEdit(cfg: SystemConfigModel) {
    setSelected(cfg);
    setInputValue(cfg.is_sensitive ? '' : cfg.config_value ?? '');
  }

  function closeModal() {
    setSelected(null);
    setInputValue('');
  }

  async function handleSave() {
    if (!selected) return;
    setSaving(true);
    try {
      await actualizarConfiguracionService(selected.id, inputValue || null);
      SimpleAlert('Configuración actualizada correctamente.', 3000, 'success');
      setReload((r) => r + 1);
      closeModal();
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'Ocurrió un error al guardar.';
      SimpleAlert(message, 3000, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}>Configuración del Sistema</PageTitle>

      {loading ? (
        <div className="d-flex justify-content-center py-10">
          <span className="spinner-border text-primary" />
        </div>
      ) : (
        categories.map((cat) => (
          <div className="card mb-6" key={cat}>
            <div className="card-header border-0 pt-5">
              <h3 className="card-title fw-bold fs-4">
                {CATEGORY_LABELS[cat] ?? cat}
              </h3>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                  <thead>
                    <tr className="fw-bold text-muted">
                      <th style={{ width: '25%' }}>Parámetro</th>
                      <th style={{ width: '35%' }}>Descripción</th>
                      <th style={{ width: '25%' }}>Valor actual</th>
                      <th className="text-end">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped[cat].map((cfg) => (
                      <tr key={cfg.id}>
                        <td>
                          <span className="text-dark fw-bold d-block">{cfg.label}</span>
                          <code className="text-muted fs-8">{cfg.config_key}</code>
                        </td>
                        <td className="text-muted fs-7">{cfg.description ?? '—'}</td>
                        <td>
                          {cfg.config_value ? (
                            cfg.is_sensitive ? (
                              <span className="badge badge-light-success fw-bold">Configurado</span>
                            ) : (
                              <code className="fs-7">{cfg.config_value}</code>
                            )
                          ) : (
                            <span className="badge badge-light-warning fw-bold">Sin configurar</span>
                          )}
                        </td>
                        <td className="text-end">
                          {cfg.is_editable && (
                            <button
                              className="btn btn-icon btn-bg-light btn-active-color-primary btn-sm"
                              title="Editar"
                              onClick={() => openEdit(cfg)}
                            >
                              <KTSVG
                                path="/media/icons/duotune/art/art005.svg"
                                className="svg-icon-3"
                              />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))
      )}

      <Modal
        id="kt_modal_system_config"
        tabIndex={-1}
        aria-hidden="true"
        dialogClassName="modal-dialog modal-dialog-centered mw-500px"
        show={!!selected}
        onHide={closeModal}
      >
        {selected && (
          <div className="modal-content">
            <div className="modal-header">
              <h2>Editar configuración</h2>
              <div
                className="btn btn-sm btn-icon btn-active-color-primary"
                onClick={closeModal}
              >
                <KTSVG className="svg-icon-2" path="/media/icons/duotune/arrows/arr061.svg" />
              </div>
            </div>
            <div className="modal-body mx-5">
              <div className="mb-5">
                <span className="fw-bold fs-5">{selected.label}</span>
                {selected.description && (
                  <div className="text-muted fs-7 mt-1">{selected.description}</div>
                )}
              </div>
              <div className="fv-row mb-7">
                <label className="fw-bold fs-6 mb-2">Valor</label>
                <input
                  className="form-control"
                  type={selected.is_sensitive ? 'password' : 'text'}
                  placeholder={
                    selected.is_sensitive
                      ? 'Ingrese el nuevo valor para actualizar'
                      : 'Ingrese el valor'
                  }
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  autoComplete="off"
                />
                {selected.is_sensitive && (
                  <small className="text-muted mt-2 d-block">
                    Por seguridad el valor actual no se muestra. Ingrese el nuevo valor para actualizarlo.
                  </small>
                )}
              </div>
              <div className="text-center pt-5">
                <button type="button" className="btn btn-light me-3" onClick={closeModal}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <span>
                      Guardando...
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export { SystemConfigPage };
